import type { FC } from 'react';
import { useEffect } from 'react';
import { toast } from 'sonner';
import type { PublicStorageLocationsRequest } from 'src/apis/storage/use-public-storage-locations';
import type { StorageOfferDetailsRequest } from 'src/apis/storage/use-storage-offer-details';
import { useStorageOfferEdit } from 'src/apis/storage/use-storage-offer-edit';
import type { StorageServiceRootRequest } from 'src/apis/storage/use-storage-service-root';
import { Button } from 'src/components/buttons-and-actions/button';
import { Card } from 'src/components/data-display/card';
import { Form } from 'src/components/form/form';
import { PageHeader } from 'src/components/layout/page-header';
import type { Successful } from 'src/hooks/use-axios';
import { useNow } from 'src/hooks/use-now';
import { useReferrer } from 'src/hooks/use-referrer';
import { useTitle } from 'src/hooks/use-title';
import { StorageOfferFields } from 'src/pages/storage/offers-new/create/fields';
import {
  formValuesToWriteData,
  getStorageOfferSchema,
} from 'src/pages/storage/offers-new/create/schema';
import type { StorageOfferFormValues } from 'src/pages/storage/offers-new/create/schema';
import {
  mapProductComponentsFromDetails,
  mapTypeOfProductToForm,
} from 'src/pages/storage/offers-new/utils';
import { Forbidden } from 'src/utils/errors';

type Props = {
  storageServiceRoot: Successful<StorageServiceRootRequest>;
  storageLocations: Successful<PublicStorageLocationsRequest>;
  storageOfferDetails: Successful<StorageOfferDetailsRequest>;
};

export const StorageOfferEditForm: FC<Props> = ({
  storageServiceRoot,
  storageLocations,
  storageOfferDetails,
}) => {
  const details = storageOfferDetails.response.data;
  const editLink = details._links.editStorageOffer;
  const prepareFileUploadLink =
    storageServiceRoot.response.data._links?.prepareFileUpload;
  const isPublishedOffer = details.offerStatus === 'PUBLISHED';

  if (!editLink || !prepareFileUploadLink) {
    throw new Forbidden();
  }

  const { productType, typeOfProductUnit } = mapTypeOfProductToForm(
    details.typeOfProduct
  );

  const attachedFiles =
    details.attachedFiles.length > 0
      ? details.attachedFiles.map((f) => ({
          file: { id: f.id, fileName: f.fileName },
        }))
      : [{ file: null }];

  const title = `Edit ${details.offerName}`;
  useTitle(title);

  const referrer = useReferrer({
    label: details.offerName,
    location: `/storage/offers-new/${details.offerId}`,
  });

  const editOffer = useStorageOfferEdit();
  const now = useNow();

  useEffect(() => {
    if (!editOffer.response) {
      return;
    }
    toast.success('Storage offer was successfully edited.');
    referrer.goTo();
    // oxlint-disable-next-line exhaustive-deps
  }, [editOffer.response]);

  return (
    <div
      className={`
        grid items-center gap-xs
        legacy:gap-[1rem]
      `}
    >
      <PageHeader title={title} backLink={referrer.backLink} />

      <Card data-testid="edit-storage-offer-form">
        <Form
          initialValues={{
            name: details.offerName,
            storageLocationId: {
              label: details.locationName,
              value: details.locationId,
            },
            productPeriod: details.productPeriod,
            productRuntimeStart: details.productRuntime.start,
            productRuntimeEnd: details.productRuntime.end,
            publication: details.publication,
            biddingWindowStart: details.biddingWindow.start,
            biddingWindowEnd: details.biddingWindow.end,
            productType,
            typeOfProductUnit,
            allocationMethod: details.allocationMethod,
            productName: details.productName,
            availableQuantity: details.availableQuantity,
            reservePrice: details.reservePrice?.amount ?? null,
            productComponents: mapProductComponentsFromDetails(
              details.productComponents
            ),
            offerSpecificNote: details.offerSpecificNote ?? null,
            attachedFiles,
          }}
          schema={(values: StorageOfferFormValues) =>
            getStorageOfferSchema({
              now,
              allowPastPublication: isPublishedOffer,
              publication: values.publication,
              biddingWindowStart: values.biddingWindowStart,
              biddingWindowEnd: values.biddingWindowEnd,
              productRuntimeStart: values.productRuntimeStart,
              productRuntimeEnd: values.productRuntimeEnd,
              productPeriod: values.productPeriod,
            })
          }
          onSubmit={(values) => {
            const payload = formValuesToWriteData(values);
            editOffer.execute({
              url: editLink.href,
              data: payload,
            });
          }}
          constraintViolation={editOffer.error}
          cancelOrResetButton={referrer.cancelLink}
          submitButton={
            <Button
              type="submit"
              disabled={editOffer.pending}
              data-testid="edit-storage-offer"
            >
              {editOffer.pending ? 'Pending...' : 'Save Changes'}
            </Button>
          }
        >
          <StorageOfferFields
            storageLocations={storageLocations}
            prepareFileUploadLink={prepareFileUploadLink}
            disablePublication={isPublishedOffer}
          />
        </Form>
      </Card>
    </div>
  );
};
